import { decodeBase64, encodeBase64 } from "../document/base64.ts";
import { EMPTY_TILE, MAX_TILE_ID, TILE_TURN, unpackTile } from "../document/packed-tile.ts";
import { asArray, asInteger, asIntegerInRange, asJsonRecord, asNumber, asPositiveInteger, asRecord, asString, asUint32, assertUniqueIds, sortedRecord } from "../document/validate.ts";
import { COLLISION_CELLS_PER_TILE, COMPILED_COLLISION_CELLS_PER_TILE } from "./collision.ts";
import { COMPILED_SCHEMA_VERSION, type ChunkEntity, type ChunkLandmark, type ChunkLayer, type ChunkMark, type ChunkProp, type CompiledChunk } from "./chunk.ts";
import type { ChunkSurfaces } from "./surfaces.ts";
import type { ChunkZones } from "./zones.ts";
import type { ChunkDoor, ChunkDoors } from "./doors.ts";
import { MAX_CHUNK_MARKS } from "./marks.ts";
import { readChunkSources } from "./sources.ts";
import { plainChunkStand, readChunkStand } from "./stand-chunk.ts";
import { plainChunkSight, readChunkSight } from "./sight.ts";

/**
 * Canonical JSON for compiled chunks (ADR-016 item 7): fixed key order, sorted property keys, typed arrays
 * as base64 by their little-endian bytes. Equal chunks serialise to identical bytes, which is what the
 * content hash and the byte-identity criterion rest on.
 */

/**
 * Converts one layer to its plain JSON shape.
 *
 * @param layer Chunk layer.
 * @returns Plain object with fixed key order.
 */
function plainLayer(layer: ChunkLayer): Record<string, unknown> {
  switch (layer.kind) {
    case "tile":
      return { kind: layer.kind, id: layer.id, name: layer.name, variant: layer.variant, tiles: encodeBase64(layer.tiles) };
    case "prop":
      return { kind: layer.kind, id: layer.id, name: layer.name, props: layer.props.map((p) => ({ id: p.id, frame: { tileset: p.frame.tileset, tile: p.frame.tile }, x: p.x, y: p.y, flipX: p.flipX, flipY: p.flipY, sortPixels: p.sortPixels, ...(p.appearance === undefined ? {} : { appearance: p.appearance }), ...(p.order === undefined ? {} : { order: p.order }) })) };
    case "entity":
      return { kind: layer.kind, id: layer.id, name: layer.name, entities: layer.entities.map((e) => ({ id: e.id, type: e.type, x: e.x, y: e.y, fields: sortedRecord(e.fields, `entities.${e.id}.fields`), ...(e.appearance === undefined ? {} : { appearance: e.appearance }) })) };
  }
}

/**
 * Serialises a chunk to compact canonical JSON. `collision`, `zones`, `doors`, `surfaces`, `landmarks`, `stand`, `sight` and `marks` are written only when
 * the chunk carries them.
 *
 * @param chunk Client or server chunk.
 * @returns The JSON text.
 */
export function serialiseChunk(chunk: CompiledChunk): string {
  const plain: Record<string, unknown> = {
    schemaVersion: chunk.schemaVersion,
    worldId: chunk.worldId,
    mapId: chunk.mapId,
    cx: chunk.cx,
    cy: chunk.cy,
    chunkSize: chunk.chunkSize,
    tilesets: chunk.tilesets,
  };
  if (chunk.collision !== null) {
    plain["collision"] = encodeBase64(chunk.collision);
  }
  if (chunk.zones !== null) {
    plain["zones"] = { types: [...chunk.zones.types], cells: encodeBase64(chunk.zones.cells) };
  }
  if (chunk.doors !== null) {
    plain["doors"] = { doors: chunk.doors.doors.map((door) => ({ id: door.id, map: door.map, arrival: door.arrival, fields: sortedRecord(door.fields, `doors.${door.id}.fields`) })), cells: encodeBase64(chunk.doors.cells) };
  }
  if (chunk.surfaces !== undefined) {
    plain["surfaces"] = { names: [...chunk.surfaces.names], cells: encodeBase64(chunk.surfaces.cells) };
  }
  if (chunk.landmarks !== undefined && chunk.landmarks.length > 0) {
    plain["landmarks"] = chunk.landmarks.map((landmark) => ({ appearance: landmark.appearance, x: landmark.x, y: landmark.y }));
  }
  if (chunk.stand !== undefined) {
    plain["stand"] = plainChunkStand(chunk.stand);
  }
  if (chunk.sight !== undefined) {
    plain["sight"] = plainChunkSight(chunk.sight);
  }
  if (chunk.marks !== undefined && chunk.marks.length > 0) {
    plain["marks"] = chunk.marks.map((mark) => ({ id: mark.id, n: mark.n, mark: mark.mark, x: mark.x, y: mark.y }));
  }
  if (chunk.sources !== undefined && chunk.sources.length > 0) {
    plain["sources"] = chunk.sources.map((source) => ({ id: source.id, kind: source.kind, x: source.x, y: source.y, radius: source.radius, strength: source.strength, when: source.when }));
  }
  plain["layers"] = chunk.layers.map(plainLayer);
  return JSON.stringify(plain);
}

/**
 * A chunk of a compiled schema this build does not read. `found` is the chunk's version and `expected` this
 * build's, so a deploy can tell a world compiled for another build from a malformed one (ADR-054's amendment
 * of 2026-09-27, decision 6).
 */
export class ChunkSchemaError extends Error {
  /**
   * @param found The chunk's schema version.
   * @param expected The version this build reads.
   */
  public constructor(
    public readonly found: number,
    public readonly expected: number,
  ) {
    super(`schemaVersion ${found} is not ${expected}`);
    this.name = "ChunkSchemaError";
  }
}

/**
 * Reads a chunk's zones: distinct, non-blank type ids and one byte per authoring cell, each 0 or a 1-based index
 * into them.
 *
 * @param value The `zones` value.
 * @param collisionCells The chunk's cells at the authoring cell.
 * @returns The zones.
 * @throws Error naming the path of the first fault.
 */
function readChunkZones(value: unknown, collisionCells: number): ChunkZones {
  const record = asRecord(value, "zones");
  const types = asArray(record["types"], "zones.types").map((type, i) => {
    const id = asString(type, `zones.types[${i}]`);
    if (id === "") {
      throw new Error(`zones.types[${i}] is blank`);
    }
    return id;
  });
  assertUniqueIds(types, "zones.types");
  const cells = decodeBase64(asString(record["cells"], "zones.cells"));
  if (cells.length !== collisionCells) {
    throw new Error(`zones.cells has ${cells.length} cells, expected ${collisionCells}`);
  }
  for (let i = 0; i < cells.length; i += 1) {
    if ((cells[i] as number) > types.length) {
      throw new Error(`zones.cells[${i}] holds ${cells[i]}, beyond the chunk's ${types.length} zone types`);
    }
  }
  return { types, cells };
}

/**
 * Reads one door of a chunk: a non-blank id, map and arrival, and a record of fields.
 *
 * @param value The value.
 * @param path Path for error messages.
 * @returns The door.
 * @throws Error naming the path of the first fault.
 */
function readChunkDoor(value: unknown, path: string): ChunkDoor {
  const record = asRecord(value, path);
  const filled = (key: string): string => {
    const text = asString(record[key], `${path}.${key}`);
    if (text === "") {
      throw new Error(`${path}.${key} is blank`);
    }
    return text;
  };
  return { id: filled("id"), map: filled("map"), arrival: filled("arrival"), fields: asJsonRecord(record["fields"], `${path}.fields`) };
}

/**
 * Reads a chunk's doors: doors of distinct ids and one byte per authoring cell, each 0 or a 1-based index into
 * them.
 *
 * @param value The `doors` value.
 * @param collisionCells The chunk's cells at the authoring cell.
 * @returns The doors.
 * @throws Error naming the path of the first fault.
 */
function readChunkDoors(value: unknown, collisionCells: number): ChunkDoors {
  const record = asRecord(value, "doors");
  const doors = asArray(record["doors"], "doors.doors").map((door, i) => readChunkDoor(door, `doors.doors[${i}]`));
  assertUniqueIds(doors.map((door) => door.id), "doors.doors");
  const cells = decodeBase64(asString(record["cells"], "doors.cells"));
  if (cells.length !== collisionCells) {
    throw new Error(`doors.cells has ${cells.length} cells, expected ${collisionCells}`);
  }
  for (let i = 0; i < cells.length; i += 1) {
    if ((cells[i] as number) > doors.length) {
      throw new Error(`doors.cells[${i}] holds ${cells[i]}, beyond the chunk's ${doors.length} doors`);
    }
  }
  return { doors, cells };
}

const BYTES_PER_U32 = 4;
const MAX_APPEARANCE = 65535;
const SIXTEENTHS = 16;
const MAX_LANDMARKS = 4096;
const MAX_LANDMARK_APPEARANCE = MAX_APPEARANCE - 1;

/**
 * Reads a chunk's landmarks: at most 4,096 of them, each an appearance index the snapshot carries and a feet point
 * in whole sixteenths inside the chunk, its far edge included, since a point rounds to the grain.
 *
 * @param value The `landmarks` value.
 * @param cx The chunk's column.
 * @param cy The chunk's row.
 * @param chunkSize The chunk's size in tiles.
 * @returns The landmarks.
 * @throws Error naming the path of the first fault.
 */
function readChunkLandmarks(value: unknown, cx: number, cy: number, chunkSize: number): ChunkLandmark[] {
  const items = asArray(value, "landmarks");
  if (items.length > MAX_LANDMARKS) {
    throw new Error(`landmarks has ${items.length} entries, at most ${MAX_LANDMARKS}`);
  }
  const span = chunkSize * SIXTEENTHS;
  return items.map((item, i) => {
    const record = asRecord(item, `landmarks[${i}]`);
    const at = (key: "x" | "y", origin: number): number => {
      const point = asInteger(record[key], `landmarks[${i}].${key}`);
      if (point < origin || point > origin + span) {
        throw new Error(`landmarks[${i}].${key} ${point} is outside the chunk's [${origin}, ${origin + span}]`);
      }
      return point;
    };
    return { appearance: asIntegerInRange(record["appearance"], MAX_LANDMARK_APPEARANCE, `landmarks[${i}].appearance`), x: at("x", cx * span), y: at("y", cy * span) };
  });
}

const MAX_MARK_INDEX = 65534;
const MAX_MARK_POINT = 65535;
const MAX_MARK_CHARS = 64;

/**
 * Reads a chunk's marked props: at most `MAX_CHUNK_MARKS` of them, each an authored id, an index of two bytes that no
 * other mark of the chunk repeats, the game's mark of 1 to 64 characters, and a feet point in whole sixteenths.
 *
 * @param value The `marks` value.
 * @returns The marks.
 * @throws Error naming the path of the first fault.
 */
function readChunkMarks(value: unknown): ChunkMark[] {
  const items = asArray(value, "marks");
  if (items.length > MAX_CHUNK_MARKS) {
    throw new Error(`marks has ${items.length} entries, at most ${MAX_CHUNK_MARKS}`);
  }
  const seen = new Map<number, number>();
  return items.map((item, i) => {
    const record = asRecord(item, `marks[${i}]`);
    const id = asString(record["id"], `marks[${i}].id`);
    const mark = asString(record["mark"], `marks[${i}].mark`);
    if (id === "") {
      throw new Error(`marks[${i}].id is blank`);
    }
    if (mark === "" || mark.length > MAX_MARK_CHARS) {
      throw new Error(`marks[${i}].mark is ${mark.length} characters, not 1 to ${MAX_MARK_CHARS}`);
    }
    const n = asIntegerInRange(record["n"], MAX_MARK_INDEX, `marks[${i}].n`);
    const earlier = seen.get(n);
    if (earlier !== undefined) {
      throw new Error(`marks[${i}].n ${n} repeats marks[${earlier}]`);
    }
    seen.set(n, i);
    return { id, n, mark, x: asIntegerInRange(record["x"], MAX_MARK_POINT, `marks[${i}].x`), y: asIntegerInRange(record["y"], MAX_MARK_POINT, `marks[${i}].y`) };
  });
}

/**
 * Reads a chunk's surfaces: distinct, non-blank names and one byte per tile, each 0 or a 1-based index into them.
 *
 * @param value The `surfaces` value.
 * @param tiles The chunk's tiles.
 * @returns The surfaces.
 * @throws Error naming the path of the first fault.
 */
function readChunkSurfaces(value: unknown, tiles: number): ChunkSurfaces {
  const record = asRecord(value, "surfaces");
  const names = asArray(record["names"], "surfaces.names").map((name, i) => {
    const id = asString(name, `surfaces.names[${i}]`);
    if (id.trim() === "") {
      throw new Error(`surfaces.names[${i}] is blank`);
    }
    return id;
  });
  assertUniqueIds(names, "surfaces.names");
  const cells = decodeBase64(asString(record["cells"], "surfaces.cells"));
  if (cells.length !== tiles) {
    throw new Error(`surfaces.cells has ${cells.length} cells, expected ${tiles}`);
  }
  for (let i = 0; i < cells.length; i += 1) {
    if ((cells[i] as number) > names.length) {
      throw new Error(`surfaces.cells[${i}] holds ${cells[i]}, beyond the chunk's ${names.length} surface names`);
    }
  }
  return { names, cells };
}

/**
 * Reads a serialised chunk back, validating its shape: schema version, grid sizes against `chunkSize`,
 * packed tiles against the tileset table, layer kinds, and the same prop and entity rules as the document
 * loader.
 *
 * @param text Chunk JSON as `serialiseChunk` wrote it.
 * @returns The chunk.
 * @throws Error naming the path of the first fault.
 */
export function deserialiseChunk(text: string): CompiledChunk {
  const record = asRecord(JSON.parse(text), "chunk");
  const schemaVersion = asInteger(record["schemaVersion"], "schemaVersion");
  if (schemaVersion !== COMPILED_SCHEMA_VERSION) {
    throw new ChunkSchemaError(schemaVersion, COMPILED_SCHEMA_VERSION);
  }
  const chunkSize = asPositiveInteger(record["chunkSize"], "chunkSize");
  const cells = chunkSize * chunkSize;
  const authoringCells = cells * COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE;
  const collisionCells = cells * COMPILED_COLLISION_CELLS_PER_TILE * COMPILED_COLLISION_CELLS_PER_TILE;
  const tilesets = asArray(record["tilesets"], "tilesets").map((t, i) => asString(t, `tilesets[${i}]`));
  let collision: Uint8Array | null = null;
  if (record["collision"] !== undefined) {
    collision = decodeBase64(asString(record["collision"], "collision"));
    if (collision.length !== collisionCells) {
      throw new Error(`collision has ${collision.length} cells, expected ${collisionCells}`);
    }
  }
  const zones = record["zones"] === undefined ? null : readChunkZones(record["zones"], authoringCells);
  const doors = record["doors"] === undefined ? null : readChunkDoors(record["doors"], authoringCells);
  const surfaces = record["surfaces"] === undefined ? undefined : readChunkSurfaces(record["surfaces"], cells);
  const landmarks = record["landmarks"] === undefined ? undefined : readChunkLandmarks(record["landmarks"], asInteger(record["cx"], "cx"), asInteger(record["cy"], "cy"), chunkSize);
  const stand = record["stand"] === undefined ? undefined : readChunkStand(record["stand"], chunkSize);
  const sight = record["sight"] === undefined ? undefined : readChunkSight(record["sight"], authoringCells);
  const marks = record["marks"] === undefined ? undefined : readChunkMarks(record["marks"]);
  const sources = record["sources"] === undefined ? undefined : readChunkSources(record["sources"]);
  const layers = asArray(record["layers"], "layers").map((item, i) => readChunkLayer(item, cells, tilesets, `layers[${i}]`));
  return {
    schemaVersion,
    worldId: asString(record["worldId"], "worldId"),
    mapId: asString(record["mapId"], "mapId"),
    cx: asInteger(record["cx"], "cx"),
    cy: asInteger(record["cy"], "cy"),
    chunkSize,
    tilesets,
    collision,
    zones,
    doors,
    ...(surfaces === undefined ? {} : { surfaces }),
    ...(landmarks === undefined || landmarks.length === 0 ? {} : { landmarks }),
    ...(stand === undefined || stand.cells.length === 0 ? {} : { stand }),
    ...(sight === undefined ? {} : { sight }),
    ...(marks === undefined || marks.length === 0 ? {} : { marks }),
    ...(sources === undefined || sources.length === 0 ? {} : { sources }),
    layers,
  };
}

/**
 * Reads one chunk layer.
 *
 * @param value Candidate.
 * @param cells Cells per chunk.
 * @param tilesets Tileset id table.
 * @param path Path for errors.
 * @returns The layer.
 * @throws Error on an unknown kind or a malformed layer.
 */
function readChunkLayer(value: unknown, cells: number, tilesets: readonly string[], path: string): ChunkLayer {
  const record = asRecord(value, path);
  const kind = asString(record["kind"], `${path}.kind`);
  const id = asString(record["id"], `${path}.id`);
  const name = asString(record["name"], `${path}.name`);
  switch (kind) {
    case "tile": {
      const bytes = decodeBase64(asString(record["tiles"], `${path}.tiles`));
      if (bytes.length !== cells * BYTES_PER_U32) {
        throw new Error(`${path}.tiles has ${bytes.length / BYTES_PER_U32} cells, expected ${cells}`);
      }
      const tiles = new Uint32Array(cells);
      for (let i = 0; i < cells; i += 1) {
        tiles[i] = ((bytes[i * 4] as number) | ((bytes[i * 4 + 1] as number) << 8) | ((bytes[i * 4 + 2] as number) << 16) | ((bytes[i * 4 + 3] as number) << 24)) >>> 0;
        const packed = tiles[i] as number;
        if (packed !== EMPTY_TILE && (packed & TILE_TURN) !== 0) {
          throw new Error(`${path}.tiles[${i}] carries a quarter turn, which this build does not read`);
        }
        if (packed !== EMPTY_TILE && unpackTile(packed).tilesetIndex >= tilesets.length) {
          throw new Error(`${path}.tiles[${i}] refers to tileset index ${unpackTile(packed).tilesetIndex}, beyond ${tilesets.length} tilesets`);
        }
      }
      const variant = record["variant"] === null ? null : asString(record["variant"], `${path}.variant`);
      return { kind, id, name, variant, tiles };
    }
    case "prop":
      return { kind, id, name, props: asArray(record["props"], `${path}.props`).map((p, i) => readChunkProp(p, tilesets, `${path}.props[${i}]`)) };
    case "entity":
      return { kind, id, name, entities: asArray(record["entities"], `${path}.entities`).map((e, i) => readChunkEntity(e, `${path}.entities[${i}]`)) };
    default:
      throw new Error(`${path}.kind ${kind} is not a chunk layer kind`);
  }
}

/**
 * Reads a prop of a chunk layer. `appearance` is present only on a prop driven by presence (ADR-058
 * amendment, P1.3-T4); `order`, its tie key (P1.6-T42), on every prop of a chunk compiled with it, and a
 * chunk written before it loads without one.
 *
 * @param value Candidate.
 * @param tilesets Tileset id table.
 * @param path Path for errors.
 * @returns The prop.
 * @throws Error naming the path when a field is malformed or the appearance index is not one the snapshot's
 *   field carries, or the tie key is not a whole number.
 */
function readChunkProp(value: unknown, tilesets: readonly string[], path: string): ChunkProp {
  const record = asRecord(value, path);
  const frame = asRecord(record["frame"], `${path}.frame`);
  const tileset = asString(frame["tileset"], `${path}.frame.tileset`);
  if (!tilesets.includes(tileset)) {
    throw new Error(`${path}.frame.tileset ${tileset} is not in the chunk's tilesets`);
  }
  return {
    id: asString(record["id"], `${path}.id`),
    frame: { tileset, tile: asIntegerInRange(frame["tile"], MAX_TILE_ID, `${path}.frame.tile`) },
    x: asNumber(record["x"], `${path}.x`),
    y: asNumber(record["y"], `${path}.y`),
    flipX: record["flipX"] === true,
    flipY: record["flipY"] === true,
    sortPixels: asInteger(record["sortPixels"], `${path}.sortPixels`),
    ...(record["appearance"] === undefined ? {} : { appearance: asUint16(record["appearance"], `${path}.appearance`) }),
    ...(record["order"] === undefined ? {} : { order: asUint32(record["order"], `${path}.order`) }),
  };
}

/**
 * Reads an entity of a chunk layer. `appearance` is present only on an entity the compiler lowered from a
 * behaviour prop (ADR-053).
 *
 * @param value Candidate.
 * @param path Path for errors.
 * @returns The entity.
 */
function readChunkEntity(value: unknown, path: string): ChunkEntity {
  const record = asRecord(value, path);
  return {
    id: asString(record["id"], `${path}.id`),
    type: asString(record["type"], `${path}.type`),
    x: asNumber(record["x"], `${path}.x`),
    y: asNumber(record["y"], `${path}.y`),
    fields: asJsonRecord(record["fields"], `${path}.fields`),
    ...(record["appearance"] === undefined ? {} : { appearance: asUint16(record["appearance"], `${path}.appearance`) }),
  };
}

/**
 * Reads an appearance index.
 *
 * @param value Candidate.
 * @param path Path for errors.
 * @returns The index.
 * @throws Error when the value is not an integer the snapshot's field carries.
 */
function asUint16(value: unknown, path: string): number {
  return asIntegerInRange(value, MAX_APPEARANCE, path);
}
